import { keepPreviousData, useQuery } from '@tanstack/react-query';
import type {
  ColumnSort,
  OnChangeFn,
  SortingState,
} from '@tanstack/react-table';
import {
  flexRender,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Icon } from '~/components';
import { DataUnavailable } from '~/components/DataUnavailable';
import { ErrorToast } from '~/components/ErrorToast/ErrorToast';
import { STICKY_TABLE_HEADER_CLASS } from '~/components/ScrollCollapse/constants/classes';
import {
  Table,
  TableHead,
  TableHeader,
  TableRow,
} from '~/modules/artist-roster/components/Table';
import { TableHeaderBackground } from '~/modules/artist-roster/components/Table/components/TableHeaderBackground';
import { useArtistTracksQuery } from '~/modules/artist-roster/hooks/useArtistTracksQuery';
import { NotFoundError } from '~/modules/artist-roster/lib/errors';
import { getBenchmarkTargets } from '~/modules/artist-roster/lib/trackTarget';
import { isNotError } from '~/modules/artist-roster/lib/utils/isNotError';
import { PreReleaseTableBody } from '~/modules/artist-roster/pages/artist-details/pages/artist-pre-releases/components/PreReleaseTable/PreReleaseTableBody';
import { usePreReleasesUrlParams } from '~/modules/artist-roster/pages/artist-details/pages/artist-pre-releases/hooks/usePreReleasesUrlParams/usePreReleasesUrlParams';
import type {
  ArtistDetails,
  AssetFamily,
  BenchmarkTarget,
} from '~/modules/artist-roster/types';

import { usePreReleaseTableColumns } from './usePreReleaseTableColumns';

export interface PreReleaseTableMeta {
  artistName: string;
  filtersCount: number;
  filtersApplied: boolean;
  uaid: number | null;
  partyId: number;
}

interface PreReleaseTableProps {
  uaid: number | null;
  artistData: ArtistDetails;
  onSortingChange?: OnChangeFn<SortingState>;
  columnSort?: ColumnSort;
  handleSetBenchmarkButtonClick: () => void;
}

export function PreReleaseTable({
  uaid,
  artistData,
  columnSort,
  onSortingChange,
  handleSetBenchmarkButtonClick,
}: PreReleaseTableProps) {
  const { t } = useTranslation('roster');
  const [isErrorToastOpen, setIsErrorToastOpen] = useState(false);

  const { pageNumber, filters } = usePreReleasesUrlParams();
  const { releaseType, searchText } = filters;

  const {
    data: tracksData,
    error: tracksError,
    isLoading: isLoadingTracks,
    isFetching: isFetchingTracks,
  } = useArtistTracksQuery({
    uaid,
    columnSort,
    pageNumber,
    searchText,
    releaseType,
    isReleased: false,
  });

  const tracksDataItems = useMemo(() => tracksData?.items || [], [tracksData]);

  const familyIds = useMemo(
    () => [...new Set(tracksDataItems?.map((track) => track.id))].sort(),
    [tracksDataItems],
  );

  const { data: benchmarkData, isLoading: isLoadingBenchmark } = useQuery({
    queryKey: ['benchmark-tracks-performance', familyIds],
    queryFn: () => getBenchmarkTargets({ familyIds }),
    enabled: !isLoadingTracks && familyIds.length > 0,
    placeholderData: keepPreviousData,
  });

  const trackAndBenchmarkData = useMemo<AssetFamily[]>(() => {
    if (!benchmarkData) {
      return tracksDataItems;
    }

    const isError = benchmarkData.some((p) => isNotError(p, NotFoundError));

    if (isError) {
      setIsErrorToastOpen(true);
    }

    return tracksDataItems.map((track) => {
      const benchmark = benchmarkData
        .filter((p): p is BenchmarkTarget => isNotError(p))
        .find((p) => p.familyId === track.id);

      if (!benchmark) {
        return track;
      }

      return {
        ...track,
        benchmark,
      };
    });
  }, [tracksDataItems, benchmarkData]);

  const meta = {
    artistName: artistData.name,
    filtersCount: filters?.releaseType !== undefined ? 1 : 0,
    filtersApplied: filters !== undefined,
    partyId: artistData.partyId,
    uaid,
  };

  const columns = usePreReleaseTableColumns(
    handleSetBenchmarkButtonClick,
    artistData,
  );
  const table = useReactTable({
    data: trackAndBenchmarkData,
    columns,
    meta,
    getCoreRowModel: getCoreRowModel(),
    enableSortingRemoval: false,
    manualSorting: true,
    onSortingChange,
    state: {
      sorting: columnSort ? [columnSort] : undefined,
    },
  });

  if (tracksError) {
    return (
      <DataUnavailable
        title={t('errorData.title')}
        subtitle={t('errorData.sub')}
        icon={
          <Icon
            type="waveform"
            size="4x"
            variant="light"
            aria-hidden
            className="text-default"
          />
        }
      />
    );
  }

  return (
    <>
      <Table aria-label={t('prereleases.table.aria')}>
        <TableHeader className={STICKY_TABLE_HEADER_CLASS}>
          {table.getHeaderGroups().map((headerGroup) => (
            <TableRow key={headerGroup.id}>
              {headerGroup.headers.map((header, index) => {
                return (
                  <TableHead
                    key={header.id}
                    className="pr-sm text-body-sm font-normal first:w-1/3 first:pl-lg last:w-3xl"
                  >
                    {!index ? <TableHeaderBackground /> : null}
                    {header.isPlaceholder
                      ? null
                      : flexRender(
                          header.column.columnDef.header,
                          header.getContext(),
                        )}
                  </TableHead>
                );
              })}
            </TableRow>
          ))}
        </TableHeader>
        <PreReleaseTableBody
          table={table}
          meta={meta}
          artistData={artistData}
          isFetchingTracks={isFetchingTracks}
          isLoadingBenchmark={isLoadingBenchmark}
        />
      </Table>
      <ErrorToast
        isOpen={isErrorToastOpen}
        onClose={() => setIsErrorToastOpen(false)}
        title={t('benchmarks.error.title')}
        description={t('benchmarks.error.caption')}
        className="z-50 w-[333px] max-w-[333px]"
      />
    </>
  );
}

PreReleaseTable.displayName = 'PreReleaseTable';
